import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const cdkFastImageCache = new Map();

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.#cdkWarmColors();
    if (this.hasAttribute('data-cdk-birdie-preview')) {
      // Product-specific switching: does not add extra images to the BIRDIE gallery.
      target?.addEventListener('change', this.#cdkBirdieOptionChanged, { capture: true, signal });
      target?.addEventListener('click', this.#cdkBirdieOptionChanged, { capture: true, signal });
      // Restore the correct hero on direct links to a selected variant.
      queueMicrotask(() => {
        if (!this.isConnected) return;
        const picker = target?.querySelector('variant-picker[data-product-id="15447742054626"]');
        if (typeof picker?.getAllSelectedOptions === 'function') {
          this.#cdkShowBirdieOptions(picker.getAllSelectedOptions());
        }
      });
    }
    if (this.hasAttribute('data-cdk-color-media')) {
      target?.addEventListener('click', this.#cdkSelectColor, { capture: true, signal });
      target?.addEventListener('change', this.#cdkSelectColor, { capture: true, signal });
      this.#cdkWarmStableColors();
    }
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // CDK original hero first: on a plain landing page, display the approved
    // original hero photo. Explicit variant links and customer selections still
    // display the matching color media selected by Shopify.
    if (
      window.location.pathname.replace(/\/$/, '') === '/products/customizable-premium-soft-unisex-tee' &&
      !new URLSearchParams(window.location.search).has('variant') &&
      !new URLSearchParams(window.location.search).has('option_values') &&
      this.dataset.cdkColorChosen !== 'true' &&
      !this.closest('dialog')
    ) {
      const slideshow = this.slideshow;
      const originalHeroIndex = slideshow?.refs.slides?.findIndex((slide) =>
        slide.querySelector('.product-media[data-media-id="46275934388450"]')
      );
      if (typeof originalHeroIndex === 'number' && originalHeroIndex > 0) {
        slideshow.setAttribute('initial-slide', String(originalHeroIndex));
      }
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#cdkColorGeneration++;
    if (this.#cdkWarmTimer) window.clearTimeout(this.#cdkWarmTimer);
    if (this.#cdkWarmIdle) window.cancelIdleCallback?.(this.#cdkWarmIdle);
  }

  #cdkRequestGeneration = 0;
  #cdkWarmTimer = 0;
  #cdkWarmIdle = 0;

  #cdkStableMap() {
    return new Map((this.dataset.cdkColorMedia || '').split('|').filter(Boolean).map((entry) => entry.split('~')));
  }

  #cdkColorSlide(mediaId) {
    return Array.from(this.querySelectorAll('slideshow-slides > slideshow-slide')).findIndex((slide) =>
      slide.querySelector('.product-media')?.getAttribute('data-media-id') === mediaId
    );
  }

  #cdkWarmStableColors() {
    const network = navigator.connection;
    if (network?.saveData || ['slow-2g', '2g', '3g'].includes(network?.effectiveType)) return;
    const warm = () => {
      if (!this.isConnected) return;
      const limit = window.matchMedia('(max-width: 749px)').matches ? 2 : 5;
      let count = 0;
      for (const mediaId of this.#cdkStableMap().values()) {
        const index = this.#cdkColorSlide(mediaId);
        const image = this.slideshow?.refs.slides?.[index]?.querySelector('img.product-media__image');
        if (!(image instanceof HTMLImageElement) || image.complete) continue;
        image.fetchPriority = 'low';
        image.loading = 'eager';
        if (++count >= limit) break;
      }
    };
    const schedule = () => {
      if (typeof window.requestIdleCallback === 'function') {
        this.#cdkWarmIdle = window.requestIdleCallback(warm, { timeout: 1800 });
      } else {
        this.#cdkWarmTimer = window.setTimeout(warm, 700);
      }
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true, signal: this.#controller.signal });
  }

  #cdkSelectColor = (event) => {
    const control = event.target;
    if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) return;
    if (event.type === 'click' && !(control instanceof HTMLInputElement)) return;
    if (!control.closest('variant-picker') || control.closest('product-card, quick-add-dialog')) return;
    const option = control instanceof HTMLSelectElement ? control.selectedOptions[0] : control;
    if (option?.dataset.optionName?.toLowerCase() !== 'color' || option.dataset.connectedProductUrl) return;
    const mediaId = this.#cdkStableMap().get(control.value);
    if (!mediaId) return;
    const index = this.#cdkColorSlide(mediaId);
    const image = this.slideshow?.refs.slides?.[index]?.querySelector('img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return;
    const generation = ++this.#cdkColorGeneration;
    image.fetchPriority = 'high';
    image.loading = 'eager';
    const show = () => {
      if (!this.isConnected || generation !== this.#cdkColorGeneration || !image.naturalWidth) return;
      this.slideshow?.select(index, undefined, { animate: false });
    };
    if (typeof image.decode === 'function') image.decode().then(show).catch(show);
    else if (image.complete) show();
    else image.addEventListener('load', show, { once: true, signal: this.#controller.signal });
  };

  // CDK four finished products: cache the exact assigned color photo without
  // adding a layout node or waiting for Shopify's full variant section response.
  #cdkColorGeneration = 0;

  #cdkColorMap() {
    if (this.hasAttribute('data-cdk-birdie-preview')) {
      try {
        const rawBirdie = this.getAttribute('data-cdk-birdie-map') || '{}';
        return new Map(Object.entries(JSON.parse(rawBirdie)));
      } catch (error) {
        console.warn('[BIRDIE] Could not read color preview mapping', error);
        return new Map();
      }
    }
    const raw = this.getAttribute('data-cdk-fast-color-map');
    if (!raw) return new Map();
    return new Map(
      raw.split('|')
        .map((entry) => {
          const index = entry.indexOf('~');
          return index > 0 ? [entry.slice(0, index), entry.slice(index + 1)] : null;
        })
        .filter(Boolean)
    );
  }

  #cdkWarmColors() {
    const network = navigator.connection;
    // On phones, load a color photo when selected instead of warming every
    // alternative at startup. This saves mobile data and competing downloads.
    const isMobile = window.matchMedia?.('(max-width: 749px)')?.matches;
    if (!this.hasAttribute('data-cdk-fast-color-map') || isMobile || network?.saveData ||
      ['slow-2g', '2g', '3g'].includes(network?.effectiveType)) return;
    const load = () => {
      if (!this.isConnected) return;
      for (const url of this.#cdkColorMap().values()) this.#cdkLoadImage(url, 'low');
    };
    const schedule = () => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(load, { timeout: 1800 });
      } else {
        window.setTimeout(load, 700);
      }
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true, signal: this.#controller.signal });
  }

  #cdkLoadImage(url, priority = 'auto') {
    let image = cdkFastImageCache.get(url);
    if (!image) {
      image = new Image();
      image.decoding = 'async';
      image.fetchPriority = priority;
      image.src = url;
      cdkFastImageCache.set(url, image);
    }
    if (priority === 'high') image.fetchPriority = 'high';
    return image;
  }

  #cdkBirdieOptionChanged = (event) => {
    const control = event.target;
    if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) return;
    if (event.type === 'click' && !(control instanceof HTMLInputElement)) return;
    if (!['color', 'sleeve'].includes(control.dataset.optionName?.toLowerCase())) return;
    const picker = control.closest('variant-picker');
    if (!picker || picker.dataset.productId !== '15447742054626' ||
      typeof picker.getAllSelectedOptions !== 'function') return;
    const options = picker.getAllSelectedOptions();
    const name = control.dataset.optionName?.toLowerCase();
    const changed = options.find((option) => option.name?.toLowerCase() === name);
    // Correct for capture-phase click events, before the picker re-renders.
    if (changed) changed.value = control.value;
    this.#cdkShowBirdieOptions(options);
  };

  #cdkShowBirdieOptions(options) {
    const color = options?.find((option) => option.name?.toLowerCase() === 'color')?.value;
    const sleeve = options?.find((option) => option.name?.toLowerCase() === 'sleeve')?.value;
    if (!color || !sleeve) return;
    const url = this.#cdkColorMap().get(`${sleeve}::${color}`);
    if (!url) return; // Never fake a shirt color without an approved product photo.
    const generation = ++this.#cdkColorGeneration;
    const image = this.#cdkLoadImage(url, 'high');
    const show = () => {
      if (!this.isConnected || this.#cdkColorGeneration !== generation || !image.naturalWidth) return;
      const hero = this.querySelector('slideshow-slides > slideshow-slide:first-child .product-media__image');
      if (!(hero instanceof HTMLImageElement)) return;
      hero.removeAttribute('srcset');
      hero.removeAttribute('sizes');
      hero.src = url;
      hero.alt = `BIRDIE women's ${color} ${sleeve.toLowerCase()} performance polo`;
      hero.loading = 'eager';
      const thumbnail = this.querySelector('slideshow-controls .slideshow-controls__thumbnail:first-child img');
      if (thumbnail instanceof HTMLImageElement) {
        thumbnail.removeAttribute('srcset');
        thumbnail.removeAttribute('sizes');
        thumbnail.src = url;
        thumbnail.alt = hero.alt;
      }
      this.slideshow?.select(0, undefined, { animate: false });
    };
    if (image.complete && image.naturalWidth > 0) show();
    else image.addEventListener('load', show, { once: true, signal: this.#controller.signal });
  }

  #cdkPreviewColor(event) {
    if (!this.hasAttribute('data-cdk-fast-color-map') && !this.hasAttribute('data-cdk-birdie-preview')) return;
    // Combined listings can change the product itself; defer those to Shopify.
    if (event.detail?.detail?.connectedProductUrl) return;
    const options = typeof event.target.getAllSelectedOptions === 'function'
      ? event.target.getAllSelectedOptions()
      : event.detail?.selectedOptions ?? [];
    if (this.hasAttribute('data-cdk-birdie-preview')) {
      this.#cdkShowBirdieOptions(options);
      return;
    }
    const color = options.find((option) => option.name?.toLowerCase() === 'color')?.value;
    const sleeve = options.find((option) => option.name?.toLowerCase() === 'sleeve')?.value;
    const key = this.hasAttribute('data-cdk-birdie-preview') && sleeve
      ? `${sleeve}::${color}`
      : color;
    const url = this.#cdkColorMap().get(key);
    if (!url) return;

    const generation = ++this.#cdkColorGeneration;
    const image = this.#cdkLoadImage(url, 'high');
    const show = () => {
      if (!this.isConnected || this.#cdkColorGeneration !== generation) return;
      const displayImage = this.querySelector('slideshow-slides > slideshow-slide:first-child .product-media__image');
      if (!(displayImage instanceof HTMLImageElement)) return;
      displayImage.removeAttribute('srcset');
      displayImage.removeAttribute('sizes');
      displayImage.src = url;
      displayImage.alt = `Selected product color: ${color}`;
      displayImage.loading = 'eager';
      // Keep the first desktop thumbnail in sync during the fast preview,
      // until Shopify returns the fully rendered two-image variant gallery.
      const thumbnail = this.querySelector('slideshow-controls .slideshow-controls__thumbnail:first-child img');
      if (thumbnail instanceof HTMLImageElement) {
        thumbnail.removeAttribute('srcset');
        thumbnail.removeAttribute('sizes');
        thumbnail.src = url;
        thumbnail.alt = `Selected product color: ${color}`;
      }
      this.slideshow?.select(0, undefined, { animate: false });
    };
    if (image.complete && image.naturalWidth > 0) show();
    else image.addEventListener('load', show, { once: true });
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const isBirdiePreview = this.hasAttribute('data-cdk-birdie-preview');
    const persistentColors = this.hasAttribute('data-cdk-color-media') || isBirdiePreview;
    const requestGeneration = ++this.#cdkRequestGeneration;
    if (isBirdiePreview || !persistentColors) this.#cdkPreviewColor(event);

    event.promise
      .then(({ detail }) => {
        if (!this.isConnected || requestGeneration !== this.#cdkRequestGeneration || !detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;
        if (isBirdiePreview && newMediaGallery.dataset.cdkBirdiePreview === this.dataset.cdkBirdiePreview) return;
        if (persistentColors && !isBirdiePreview && newMediaGallery.dataset.cdkColorProduct === this.dataset.cdkColorProduct) return;

        // A user-selected color should retain Shopify's variant-specific image.
        if (window.location.pathname.replace(/\/$/, '') === '/products/customizable-premium-soft-unisex-tee') {
          newMediaGallery.dataset.cdkColorChosen = 'true';
        }
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

